import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../contexts/NotificationContext";
import { apiService } from "../../../backend/api";
import { getBaseUrl } from "../../../config/apiConfig";
import {
  User as UserIcon,
  CheckCircle2,
  ExternalLink,
  Settings,
  RefreshCw,
  Shield,
  Save,
  Gift,
  Plus,
  Minus,
  ShieldOff,
  KeyRound,
  Mail,
  Send,
  UserPlus,
  Copy
} from "lucide-react";

const UserDetailModal = ({
  isOpen,
  onOpenChange,
  selectedUser,
  isEditingRoles,
  setIsEditingRoles,
  allSystemRoles,
  tempSelectedRoles,
  handleRoleToggle,
  handleSaveRoles,
  isSavingRoles,
  handleUserSelect
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [bonusCredits, setBonusCredits] = useState(0);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [isSavingBonus, setIsSavingBonus] = useState(false);
  const [isResetting2FA, setIsResetting2FA] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [isResendingRegistration, setIsResendingRegistration] = useState(false);
  const [isOpeningMailClient, setIsOpeningMailClient] = useState(false);
  const [isOpeningActivationMailClient, setIsOpeningActivationMailClient] = useState(false);
  const [isCopyingResetLink, setIsCopyingResetLink] = useState(false);
  // Inline-Status pro Mail-Aktion: { ok: bool, message: string, at: Date }
  const [passwordResetStatus, setPasswordResetStatus] = useState(null);
  const [registrationResendStatus, setRegistrationResendStatus] = useState(null);
  const [mailClientStatus, setMailClientStatus] = useState(null);
  const [activationMailClientStatus, setActivationMailClientStatus] = useState(null);
  const [copyLinkStatus, setCopyLinkStatus] = useState(null);
  // Der zuletzt erzeugte Reset-Link. Nur Component-State, bewusst nirgends
  // persistiert — er ist ein vollwertiger Zugangs-Credential.
  const [resetLink, setResetLink] = useState(null);

  // Status zurücksetzen, wenn der User wechselt
  useEffect(() => {
    setPasswordResetStatus(null);
    setRegistrationResendStatus(null);
    setMailClientStatus(null);
    setActivationMailClientStatus(null);
    setCopyLinkStatus(null);
    // Nie einen Link stehen lassen, der zu einem anderen Nutzer gehört.
    setResetLink(null);
  }, [selectedUser?.username]);

  // Fetch bonus credits when user changes
  useEffect(() => {
    if (!selectedUser?.username) return;
    apiService.request(`${getBaseUrl()}/api/payment/admin/bonus/${selectedUser.username}`)
      .then(data => setBonusCredits(data.bonusDownloads || 0))
      .catch(() => setBonusCredits(0));
  }, [selectedUser?.username]);

  const handleGrantBonus = async (amount) => {
    if (!amount || amount === 0) return;
    setIsSavingBonus(true);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/payment/admin/grant-bonus`, {
        method: 'POST',
        body: JSON.stringify({
          username: selectedUser.username,
          amount,
          reason: bonusReason || undefined
        })
      });
      setBonusCredits(data.newBonus);
      setBonusAmount('');
      setBonusReason('');
      showNotification(`Bonus-Guthaben: ${data.previousBonus} → ${data.newBonus}`, 'success');
    } catch (err) {
      showNotification('Fehler: ' + err.message, 'error');
    } finally {
      setIsSavingBonus(false);
    }
  };

  const handleReset2FA = async () => {
    if (!selectedUser?.username) return;
    setIsResetting2FA(true);
    try {
      await apiService.request(`${getBaseUrl()}/api/user/2fa/invalidate`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      showNotification(`2FA für ${selectedUser.username} wurde zurückgesetzt.`, 'success');
    } catch (err) {
      showNotification('2FA-Reset fehlgeschlagen: ' + err.message, 'error');
    } finally {
      setIsResetting2FA(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!selectedUser?.username) return;
    setIsSendingPasswordReset(true);
    setPasswordResetStatus(null);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/admin/send-password-reset`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      const msg = data.message || `Passwort-Reset-Mail an ${selectedUser.email} verschickt.`;
      setPasswordResetStatus({ ok: true, message: msg, at: new Date() });
      showNotification(msg, 'success');
    } catch (err) {
      const msg = 'Versand fehlgeschlagen: ' + err.message;
      setPasswordResetStatus({ ok: false, message: msg, at: new Date() });
      showNotification(msg, 'error');
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleResendRegistration = async () => {
    if (!selectedUser?.username) return;
    setIsResendingRegistration(true);
    setRegistrationResendStatus(null);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/admin/resend-registration-email`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      const msg = data.message || `Registrierungs-Mail an ${selectedUser.email} verschickt.`;
      setRegistrationResendStatus({ ok: true, message: msg, at: new Date() });
      showNotification(msg, 'success');
    } catch (err) {
      const msg = 'Versand fehlgeschlagen: ' + err.message;
      setRegistrationResendStatus({ ok: false, message: msg, at: new Date() });
      showNotification(msg, 'error');
    } finally {
      setIsResendingRegistration(false);
    }
  };

  const formatStatusTime = (d) => d ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  // Robustes mailto-Öffnen über temporären Anchor — auf Mobile-Browsern
  // (insb. iOS Safari, Android Chrome) ist `window.location.href = 'mailto:…'`
  // teils unzuverlässig oder wird durch Popup-Blocker abgewürgt. Ein
  // synchroner Klick auf einen Anchor ist der konsistente Workaround.
  const openMailto = (toEmail, subject, body) => {
    const mailto = `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const a = document.createElement('a');
    a.href = mailto;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // navigator.clipboard braucht einen Secure Context und kann je nach Browser
  // oder fehlender Berechtigung trotzdem ablehnen — deshalb der execCommand-
  // Fallback. Gibt zurück, ob das Kopieren geklappt hat; der Aufrufer zeigt
  // den Link zusätzlich an, damit manuelles Markieren immer möglich bleibt.
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // weiter zum Fallback
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  // Reset-Link erzeugen und in die Zwischenablage legen — ohne Mailversand.
  // Für die Fälle, in denen die automatische Mail beim Empfänger nicht ankommt
  // (Spam-Filter, Reject) und der Link über Telefon, Chat oder Ticket raus soll.
  const handleCopyResetLink = async () => {
    if (!selectedUser?.username) return;
    setIsCopyingResetLink(true);
    setCopyLinkStatus(null);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/admin/generate-password-reset-link`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      if (!data?.link) {
        throw new Error('Server hat keinen Link zurückgegeben');
      }

      setResetLink(data.link);
      const copied = await copyToClipboard(data.link);
      const msg = copied
        ? 'Reset-Link kopiert — gültig 48 Stunden.'
        : 'Link erzeugt, Kopieren wurde vom Browser blockiert — bitte unten markieren.';
      setCopyLinkStatus({ ok: copied, message: msg, at: new Date() });
      showNotification(msg, copied ? 'success' : 'error');
    } catch (err) {
      const msg = 'Link-Erstellung fehlgeschlagen: ' + (err.message || '');
      setResetLink(null);
      setCopyLinkStatus({ ok: false, message: msg, at: new Date() });
      showNotification(msg, 'error');
    } finally {
      setIsCopyingResetLink(false);
    }
  };

  // Manuelle Passwort-Reset-Mail: holt einen Set-Password-Link (48 h) vom
  // Backend, baut Mailto-Body mit Anrede, Username + Login-Hinweis, öffnet
  // Standard-Mailprogramm. Für Bestandskunden die ihr Passwort vergessen
  // haben — keine Aktivierungs-Begrüßung, nur Reset.
  const handleOpenMailClient = async () => {
    if (!selectedUser?.username) return;
    setIsOpeningMailClient(true);
    setMailClientStatus(null);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/admin/generate-password-reset-link`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      if (!data?.link || !data?.email) {
        throw new Error('Server hat keinen Link/keine Email zurückgegeben');
      }

      const firstName = data.firstName || selectedUser.firstName || '';
      const lastName = data.lastName || selectedUser.lastName || '';
      const greetingName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'zusammen';
      const usernameLine = data.username
        ? `\nIhr Benutzername lautet: ${data.username}\n(Sie können sich alternativ auch mit Ihrer E-Mail-Adresse einloggen.)\n`
        : '';
      const subject = 'Passwort zurücksetzen — Video-3D-Scan-Plattform';
      const body =
`Hallo ${greetingName},

über den folgenden Link können Sie ein neues Passwort für die Video-3D-Scan-Plattform festlegen:

${data.link}
${usernameLine}
Der Link ist 48 Stunden gültig. Sollte er ablaufen, melden Sie sich bitte kurz bei uns, dann senden wir Ihnen einen neuen.

Bei Fragen erreichen Sie uns jederzeit unter info@3d-medico.de.

Viele Grüße
Ihr 3D-Medico-Team`;

      openMailto(data.email, subject, body);

      const msg = `Mailprogramm geöffnet — Link an ${data.email} eingefügt.`;
      setMailClientStatus({ ok: true, message: msg, at: new Date() });
      showNotification(msg, 'success');
    } catch (err) {
      const msg = 'Link-Erstellung fehlgeschlagen: ' + (err.message || '');
      setMailClientStatus({ ok: false, message: msg, at: new Date() });
      showNotification(msg, 'error');
    } finally {
      setIsOpeningMailClient(false);
    }
  };

  // Manuelle Aktivierungs-/Willkommens-Mail: holt einen Set-Password-Link mit
  // 14-Tage-Gültigkeit (Aktivierungs-Flow → Kunde hat genug Zeit), baut einen
  // Begrüßungs-Body mit Anrede, Username, Login-Hinweis (auch via Email) und
  // Set-Password-Link. Für Neu-Kunden, deren auto-Welcome-Mail im Spam landet.
  const handleOpenActivationMailClient = async () => {
    if (!selectedUser?.username) return;
    setIsOpeningActivationMailClient(true);
    setActivationMailClientStatus(null);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/admin/generate-activation-link`, {
        method: 'POST',
        body: JSON.stringify({ username: selectedUser.username })
      });
      if (!data?.link || !data?.email) {
        throw new Error('Server hat keinen Link/keine Email zurückgegeben');
      }

      const firstName = data.firstName || selectedUser.firstName || '';
      const lastName = data.lastName || selectedUser.lastName || '';
      const greetingName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'zusammen';
      const username = data.username || selectedUser.username || '';
      const subject = 'Willkommen bei der Video-3D-Scan-Plattform — Ihr Zugang ist aktiv';
      const body =
`Hallo ${greetingName},

herzlich willkommen bei der Video-3D-Scan-Plattform von 3D-Medico!

Ihr Konto wurde von uns freigeschaltet. Über folgenden Link können Sie Ihr Passwort festlegen und sich anschließend einloggen:

${data.link}

Der Link ist 14 Tage gültig.

Ihre Zugangsdaten:
• Benutzername: ${username}
• E-Mail:       ${data.email}

Beim Login können Sie wahlweise den Benutzernamen oder Ihre E-Mail-Adresse verwenden.

Bei Fragen oder Problemen erreichen Sie uns jederzeit unter info@3d-medico.de — wir helfen Ihnen gerne weiter.

Viele Grüße
Ihr 3D-Medico-Team`;

      openMailto(data.email, subject, body);

      const msg = `Mailprogramm geöffnet — Aktivierungs-Link an ${data.email} eingefügt.`;
      setActivationMailClientStatus({ ok: true, message: msg, at: new Date() });
      showNotification(msg, 'success');
    } catch (err) {
      const msg = 'Link-Erstellung fehlgeschlagen: ' + (err.message || '');
      setActivationMailClientStatus({ ok: false, message: msg, at: new Date() });
      showNotification(msg, 'error');
    } finally {
      setIsOpeningActivationMailClient(false);
    }
  };

  if (!selectedUser) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden border-none shadow-2xl bg-background rounded-3xl">
        <div className="flex flex-col h-full">
          {/* Modal Header */}
          <div className="px-6 py-6 border-b flex items-center space-x-4">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
              <UserIcon size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold">{selectedUser.username}</h3>
              <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            {!isEditingRoles ? (
              <div className="space-y-6">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-xl border">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{t('adminUsers.company')}</p>
                    <p className="text-sm font-semibold">{selectedUser.company || t('adminUsers.notSpecified')}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-xl border">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{t('adminUsers.registeredOn')}</p>
                    <p className="text-sm font-semibold">{new Date(selectedUser.createdAt).toLocaleDateString('de-DE')}</p>
                  </div>
                </div>

                {/* Subscriptions Section */}
                <div>
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">{t('adminUsers.subscriptionsAndPackages')}</h4>
                  <div className="space-y-2">
                    {selectedUser.subscriptions && selectedUser.subscriptions.length > 0 ? (
                      selectedUser.subscriptions.map((sub, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-background border rounded-lg shadow-sm">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                              <CheckCircle2 size={16} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">{sub.planName}</p>
                              <p className="text-[10px] text-muted-foreground">{t('adminUsers.nextBilling')}: {new Date(sub.nextBillingDate).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">€{Number(sub.priceMonthly).toFixed(2)}</p>
                            <Badge variant={sub.status === 'aktiv' ? 'success' : 'outline'} className="text-[8px] font-bold">
                              {sub.status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center border border-dashed rounded-lg text-xs text-muted-foreground italic">
                        {t('adminUsers.noActiveSubscriptions')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bonus Credits Section */}
                <div>
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Bonus-Guthaben</h4>
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gift size={18} className="text-amber-500" />
                        <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Aktuell: {bonusCredits} Downloads</span>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800/30 text-[9px]">Kein Ablaufdatum</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={bonusAmount}
                        onChange={(e) => setBonusAmount(e.target.value)}
                        placeholder="Anzahl"
                        className="h-9 w-24 text-sm"
                      />
                      <Input
                        value={bonusReason}
                        onChange={(e) => setBonusReason(e.target.value)}
                        placeholder="Grund (optional)"
                        className="h-9 flex-1 text-sm"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!bonusAmount || isSavingBonus}
                        onClick={() => handleGrantBonus(parseInt(bonusAmount))}
                        className="h-9 text-xs font-bold border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                      >
                        <Plus size={14} className="mr-1" />
                        Hinzufügen
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!bonusAmount || isSavingBonus}
                        onClick={() => handleGrantBonus(-Math.abs(parseInt(bonusAmount)))}
                        className="h-9 text-xs font-bold border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                      >
                        <Minus size={14} className="mr-1" />
                        Abziehen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* 2FA Reset Section */}
                <div>
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">2FA / TOTP</h4>
                  <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldOff size={18} className="text-red-500" />
                        <span className="text-sm font-semibold text-red-700 dark:text-red-300">
                          {t("adminUsers.reset2fa", "2FA zurücksetzen")}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isResetting2FA}
                        onClick={handleReset2FA}
                        className="h-9 text-xs font-bold border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-400"
                      >
                        {isResetting2FA ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <ShieldOff size={14} className="mr-1" />}
                        Reset
                      </Button>
                    </div>
                    <p className="text-[10px] text-red-600/70 dark:text-red-400/70 mt-2">
                      {t("adminUsers.reset2faDesc", "Entfernt TOTP-Secret und Backup-Codes. Der Nutzer muss 2FA beim nächsten Login neu einrichten.")}
                    </p>
                  </div>
                </div>

                {/* Admin E-Mail Actions */}
                <div>
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">E-Mail Aktionen</h4>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-xl space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <KeyRound size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">
                          Passwort-Reset-Mail senden
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isSendingPasswordReset}
                        onClick={handleSendPasswordReset}
                        className="h-9 text-xs font-bold border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 flex-shrink-0"
                      >
                        {isSendingPasswordReset ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <KeyRound size={14} className="mr-1" />}
                        Senden
                      </Button>
                    </div>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 -mt-2">
                      Sendet einen Reset-Link (gültig 48 Stunden) an {selectedUser.email}.
                    </p>
                    {passwordResetStatus && (
                      <div className={`text-[11px] font-medium px-2 py-1.5 rounded-md flex items-start gap-1.5 -mt-1 ${
                        passwordResetStatus.ok
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        <span className="flex-shrink-0">{passwordResetStatus.ok ? '✓' : '✗'}</span>
                        <span className="flex-1">
                          {passwordResetStatus.message}
                          <span className="opacity-60 ml-1">({formatStatusTime(passwordResetStatus.at)})</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-blue-200 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">
                          Registrierungs-Mail erneut senden
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isResendingRegistration}
                        onClick={handleResendRegistration}
                        className="h-9 text-xs font-bold border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 flex-shrink-0"
                      >
                        {isResendingRegistration ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
                        Senden
                      </Button>
                    </div>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 -mt-2">
                      Sendet die Willkommens-/Bestätigungsmail erneut an {selectedUser.email}.
                    </p>
                    {registrationResendStatus && (
                      <div className={`text-[11px] font-medium px-2 py-1.5 rounded-md flex items-start gap-1.5 -mt-1 ${
                        registrationResendStatus.ok
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        <span className="flex-shrink-0">{registrationResendStatus.ok ? '✓' : '✗'}</span>
                        <span className="flex-1">
                          {registrationResendStatus.message}
                          <span className="opacity-60 ml-1">({formatStatusTime(registrationResendStatus.at)})</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-blue-200 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 min-w-0">
                        <Send size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">
                          Passwort-Reset manuell aus eigenem Programm
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isOpeningMailClient}
                        onClick={handleOpenMailClient}
                        className="h-9 text-xs font-bold border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 flex-shrink-0"
                      >
                        {isOpeningMailClient ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                        Vorbereiten
                      </Button>
                    </div>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 -mt-2">
                      Öffnet dein Mailprogramm mit Anrede + Passwort-Reset-Link an {selectedUser.email}. Du verschickst die Mail selbst.
                    </p>
                    {mailClientStatus && (
                      <div className={`text-[11px] font-medium px-2 py-1.5 rounded-md flex items-start gap-1.5 -mt-1 ${
                        mailClientStatus.ok
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        <span className="flex-shrink-0">{mailClientStatus.ok ? '✓' : '✗'}</span>
                        <span className="flex-1">
                          {mailClientStatus.message}
                          <span className="opacity-60 ml-1">({formatStatusTime(mailClientStatus.at)})</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-blue-200 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 min-w-0">
                        <Copy size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">
                          Reset-Link kopieren
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isCopyingResetLink}
                        onClick={handleCopyResetLink}
                        className="h-9 text-xs font-bold border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 flex-shrink-0"
                      >
                        {isCopyingResetLink ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Copy size={14} className="mr-1" />}
                        Kopieren
                      </Button>
                    </div>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 -mt-2">
                      Erzeugt den Reset-Link (gültig 48 Stunden) und legt ihn in die Zwischenablage — ohne Mailversand. Für Weitergabe per Telefon, Chat oder Ticket.
                    </p>
                    {resetLink && (
                      <input
                        type="text"
                        readOnly
                        value={resetLink}
                        onFocus={(e) => e.target.select()}
                        className="w-full text-[10px] font-mono px-2 py-1.5 -mt-1 rounded-md bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800/30 text-blue-800 dark:text-blue-300 outline-none focus:border-blue-400"
                      />
                    )}
                    {copyLinkStatus && (
                      <div className={`text-[11px] font-medium px-2 py-1.5 rounded-md flex items-start gap-1.5 -mt-1 ${
                        copyLinkStatus.ok
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        <span className="flex-shrink-0">{copyLinkStatus.ok ? '✓' : '✗'}</span>
                        <span className="flex-1">
                          {copyLinkStatus.message}
                          <span className="opacity-60 ml-1">({formatStatusTime(copyLinkStatus.at)})</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-blue-200 dark:border-blue-800/30">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserPlus size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">
                          Aktivierungs-/Willkommens-Mail manuell
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isOpeningActivationMailClient}
                        onClick={handleOpenActivationMailClient}
                        className="h-9 text-xs font-bold border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-400 flex-shrink-0"
                      >
                        {isOpeningActivationMailClient ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <UserPlus size={14} className="mr-1" />}
                        Vorbereiten
                      </Button>
                    </div>
                    <p className="text-[10px] text-blue-600/70 dark:text-blue-400/70 -mt-2">
                      Öffnet dein Mailprogramm mit Begrüßung, Username + Set-Password-Link (gültig 14 Tage) an {selectedUser.email}.
                    </p>
                    {activationMailClientStatus && (
                      <div className={`text-[11px] font-medium px-2 py-1.5 rounded-md flex items-start gap-1.5 -mt-1 ${
                        activationMailClientStatus.ok
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        <span className="flex-shrink-0">{activationMailClientStatus.ok ? '✓' : '✗'}</span>
                        <span className="flex-1">
                          {activationMailClientStatus.message}
                          <span className="opacity-60 ml-1">({formatStatusTime(activationMailClientStatus.at)})</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent Scans Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('adminUsers.recentScans')}</h4>
                    <span className="text-[10px] font-medium text-muted-foreground">{t('adminUsers.total')}: {selectedUser.scans?.length || 0}</span>
                  </div>
                  <div className="space-y-2">
                    {selectedUser.scans && selectedUser.scans.length > 0 ? (
                      selectedUser.scans.slice(0, 3).map((scan, i) => (
                        <div key={i} className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors border border-transparent hover:border-border">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-muted-foreground">
                              <UserIcon size={16} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold line-clamp-1">{scan.title}</p>
                              <p className="text-[10px] text-muted-foreground">{new Date(scan.date).toLocaleDateString('de-DE')}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-[8px] font-bold text-primary">{scan.status.toUpperCase()}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-center py-2 text-muted-foreground text-[10px] italic">{t('adminUsers.noScansYet')}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8 py-4 animate-in fade-in zoom-in-95 duration-300">
                <div className="text-center">
                   <div className="w-16 h-16 bg-primary/5 text-primary rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/10">
                    <Shield size={32} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{t('adminUsers.adjustPermissions')}</h3>
                  <p className="text-sm text-muted-foreground max-w-[280px] mx-auto mt-1">
                    {t('adminUsers.assignRolesDesc')}
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground/60 mt-2 uppercase">User ID: {selectedUser._id}</p>
                </div>

                <div className="space-y-3">
                  <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-4">{t('adminUsers.availableRoles')}</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {allSystemRoles.length > 0 ? (
                      allSystemRoles.map((role) => {
                        const isSelected = tempSelectedRoles.includes(role.name);
                        return (
                          <button
                            key={role._id}
                            onClick={() => handleRoleToggle(role.name)}
                            className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 text-left outline-none group ${
                              isSelected
                                ? "border-primary bg-primary/5 ring-4 ring-primary/5"
                                : "border-muted-foreground/10 bg-muted/30 hover:border-muted-foreground/20 hover:bg-muted/50"
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                                isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                              }`}>
                                <Shield size={18} />
                              </div>
                              <div>
                                <p className={`font-bold text-sm ${isSelected ? "text-primary" : "text-foreground"}`}>
                                  {role.name}
                                </p>
                                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-tight">{t('adminUsers.systemRole')}</p>
                              </div>
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? "bg-primary border-primary text-primary-foreground scale-110 shadow-lg shadow-primary/20"
                                : "border-muted-foreground/30 bg-background"
                            }`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="py-8 text-center text-muted-foreground italic text-xs">
                        <RefreshCw className="animate-spin w-5 h-5 mx-auto mb-2 opacity-20" />
                        {t('adminUsers.loadingRoles')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-6 bg-muted/50 border-t flex items-center justify-between">
            {!isEditingRoles ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUserSelect(selectedUser.username)}
                  className="text-xs font-bold"
                >
                  <ExternalLink size={14} className="mr-2" />
                  {t('adminUsers.openProjects')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setIsEditingRoles(true)}
                  className="text-xs font-bold"
                >
                  <Settings size={14} className="mr-2" />
                  {t('adminUsers.editRole')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingRoles(false)}
                  className="text-xs font-bold"
                  disabled={isSavingRoles}
                >
                  {t('adminUsers.cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveRoles}
                  className="text-xs font-bold min-w-[140px]"
                  disabled={isSavingRoles}
                >
                  {isSavingRoles ? (
                    <RefreshCw size={14} className="mr-2 animate-spin" />
                  ) : (
                    <Save size={14} className="mr-2" />
                  )}
                  {t('adminUsers.saveChanges')}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;
